import { useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";

const Wrapper = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 2px;
  margin-bottom: 4px;
`;

const LabelText = styled.span`
  font-size: 0.9rem;
  font-weight: 600;
  color: #084d6e;
`;

const TooltipBubble = styled.div`
  position: fixed;
  z-index: 1000;
  background: #084d6e;
  color: #fff;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 400;
  line-height: 1.4;
  white-space: normal;
  width: max-content;
  max-width: 260px;
  text-align: center;
  box-shadow: 0 4px 14px rgba(8, 77, 110, 0.25);
  pointer-events: ${(props) => (props.$pinned ? "auto" : "none")};
  transform: translate(-50%, calc(-100% - 8px));

  &::after {
    content: "";
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 6px solid transparent;
    border-top-color: #084d6e;
  }
`;

const PinButton = styled.button`
  background: transparent;
  border: none;
  color: #9e2480;
  cursor: pointer;
  font-size: 15px;
  line-height: 1;
  padding: 2px 4px;
  border-radius: 4px;
  flex-shrink: 0;

  &:hover {
    background: rgba(158, 36, 128, 0.1);
  }

  &[aria-pressed="true"] {
    background: rgba(158, 36, 128, 0.18);
    color: #7a1d66;
  }
`;

const LabelWithTooltip = ({ label, tooltip, pinned, onTogglePin }) => {
  const wrapperRef = useRef(null);
  const [hovering, setHovering] = useState(false);
  const [coords, setCoords] = useState(null);
  const showTooltip = (hovering || pinned) && tooltip;

  const updateCoords = () => {
    if (!wrapperRef.current) return;
    const rect = wrapperRef.current.getBoundingClientRect();
    setCoords({
      top: rect.top,
      left: rect.left + rect.width / 2,
    });
  };

  useLayoutEffect(() => {
    if (!showTooltip) return undefined;

    updateCoords();
    window.addEventListener("scroll", updateCoords, true);
    window.addEventListener("resize", updateCoords);

    return () => {
      window.removeEventListener("scroll", updateCoords, true);
      window.removeEventListener("resize", updateCoords);
    };
  }, [showTooltip]);

  if (!tooltip) {
    return <LabelText style={{ display: "block", marginBottom: 4 }}>{label}</LabelText>;
  }

  return (
    <Wrapper
      ref={wrapperRef}
      onMouseEnter={() => {
        setHovering(true);
        updateCoords();
      }}
      onMouseLeave={() => setHovering(false)}
    >
      {showTooltip &&
        coords &&
        createPortal(
          <TooltipBubble
            $pinned={pinned}
            role="tooltip"
            style={{ top: coords.top, left: coords.left }}
          >
            {tooltip}
          </TooltipBubble>,
          document.body
        )}
      <LabelText>{label}</LabelText>
      <PinButton
        type="button"
        aria-label={`${pinned ? "Ocultar" : "Fixar"} explicação de ${label}`}
        aria-pressed={pinned}
        title={tooltip}
        onClick={(e) => {
          e.stopPropagation();
          onTogglePin();
        }}
      >
        ⓘ
      </PinButton>
    </Wrapper>
  );
};

export default LabelWithTooltip;
